import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { getRequest, postRequest, putRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL, PUT_URL } from 'Includes/urls';
import { useNavigate, useLocation } from 'react-router-dom';

const LEGACY_CATEGORY_CODES = ['templates', 'digital_marketing', 'dev_tasks', 'bugs'];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function extractApiList(response) {
  if (!response || response.status !== 200) return [];
  const body = response.data;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body)) return body;
  return [];
}

function findBacklogStageId(stages) {
  const list = stages || [];
  const hit = list.find((s) => String(s.code || '').toLowerCase() === 'backlog');
  return hit?.id != null ? String(hit.id) : '';
}

function TaskAdd(props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [companyLoading, setCompanyLoading] = React.useState(false);
  const [companySearch, setCompanySearch] = React.useState('');
  const [companyPage, setCompanyPage] = React.useState(1);
  const [companyHasMore, setCompanyHasMore] = React.useState(true);
  const [selectedCompany, setSelectedCompany] = React.useState(null);

  const [form, setForm] = React.useState({
    title: '',
    description: '',
    company: '',
    assigned_to: '',
    priority: 'medium',
    task_type: '',
    task_stage: '',
    deadline: '',
    planned_date: '',
    estimated_hours: '',
    actual_hours: '',
    recurring: 0,
    shift_type: '',
    trigger_time: '',
    images: [],
  });

  const [legacyCategory, setLegacyCategory] = React.useState('dev_tasks');
  const [loading, setLoading] = React.useState(false);
  const [workflowTypes, setWorkflowTypes] = React.useState([]);
  const [workflowStages, setWorkflowStages] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [selectedTaskTypeOption, setSelectedTaskTypeOption] = React.useState(null);

  const params = new URLSearchParams(location.search);
  const taskId = params.get('task_id');

  const companyReqSeq = React.useRef(0);
  const companyDebounceRef = React.useRef(null);

  const loadCompanies = React.useCallback(
    (nextPage, nextSearch, mode = 'append') => {
      const seq = ++companyReqSeq.current;

      setCompanyLoading(true);

      const reqParams = {
        limit: 10,
        pageno: nextPage,
        ...(nextSearch ? { search: nextSearch } : {}),
      };

      return getRequest(GET_URL.company.api, reqParams, props)
        .then((response) => {
          if (seq !== companyReqSeq.current) return;

          if (response && response.status === 200) {
            const data = response.data?.data || [];
            const count = response.data?.count ?? null;

            setCompanyOptions((prev) => {
              const base = mode === 'replace' ? [] : prev || [];
              const seen = new Set(base.map((r) => String(r.id)));
              const merged = [...base];

              data.forEach((row) => {
                if (!row || row.id == null) return;

                const key = String(row.id);

                if (!seen.has(key)) {
                  seen.add(key);
                  merged.push(row);
                }
              });

              return merged;
            });

            if (count != null) {
              setCompanyHasMore(nextPage * 10 < Number(count));
            } else {
              setCompanyHasMore(data.length === 10);
            }
          }
        })
        .finally(() => {
          if (seq === companyReqSeq.current) {
            setCompanyLoading(false);
          }
        });
    },
    [props]
  );

  React.useEffect(() => {
    let cancelled = false;

    getRequest(GET_URL.task_types.api, { limit: 200, pageno: 1 }, props).then(
      (response) => {
        if (cancelled) return;

        const list = extractApiList(response);
        setWorkflowTypes(list);
      }
    );

    getRequest(GET_URL.user.api, { limit: 500, pageno: 1 }, props).then(
      (response) => {
        if (cancelled) return;

        const list = extractApiList(response);
        setUsers(list);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [props]);

  React.useEffect(() => {
    if (!form.task_type) {
      setWorkflowStages([]);
      return;
    }

    getRequest(
      GET_URL.task_type_stages.api,
      {
        limit: 500,
        pageno: 1,
        task_type: form.task_type,
      },
      props
    ).then((response) => {
      const list = extractApiList(response);
      setWorkflowStages(list);
    });
  }, [form.task_type, props]);

  React.useEffect(() => {
    if (!form.task_type || !workflowStages.length) return;
    if (taskId) return;

    const backlogId = findBacklogStageId(workflowStages);

    if (!backlogId) return;

    setForm((prev) => {
      if (prev.task_stage) return prev;

      return {
        ...prev,
        task_stage: backlogId,
      };
    });
  }, [workflowStages, form.task_type, taskId]);

  React.useEffect(() => {
    if (taskId) {
      getRequest(`${GET_URL.tasks.api}${taskId}/`, {}, props).then(
        (response) => {
          const row = response?.data?.data;

          if (response && response.status === 200 && row) {
            setLegacyCategory(row.category || 'dev_tasks');

            const ttId =
              row.task_type != null ? String(row.task_type) : '';

            setForm({
              title: row.title || '',
              description: row.description || '',
              company: row.company || '',
              assigned_to: row.assigned_to != null ? String(row.assigned_to) : '',
              priority: row.priority || 'medium',
              task_type: ttId,
              task_stage:
                row.task_stage != null
                  ? String(row.task_stage)
                  : '',
              deadline: row.deadline || '',
              planned_date: row.planned_date || '',
              estimated_hours: row.estimated_hours || '',
              actual_hours: row.actual_hours || '',
              recurring: row.recurring || 0,
              shift_type: row.shift_type || '',
              trigger_time: row.trigger_time || '',
              images: row.details?.images || [],
            });
          }
        }
      );
    }
  }, [taskId, props]);

  React.useEffect(() => {
    if (!form.task_type || !workflowTypes.length) {
      setSelectedTaskTypeOption(null);
      return;
    }

    const match = workflowTypes.find(
      (t) => String(t.id) === String(form.task_type)
    );

    setSelectedTaskTypeOption(match || null);
  }, [form.task_type, workflowTypes]);

  React.useEffect(() => {
    if (companyDebounceRef.current) {
      clearTimeout(companyDebounceRef.current);
    }

    companyDebounceRef.current = setTimeout(() => {
      setCompanyPage(1);
      setCompanyHasMore(true);

      loadCompanies(1, companySearch, 'replace');
    }, 300);

    return () => {
      if (companyDebounceRef.current) {
        clearTimeout(companyDebounceRef.current);
      }
    };
  }, [companySearch, loadCompanies]);

  React.useEffect(() => {
    const id = form.company;

    if (!id) {
      setSelectedCompany(null);
      return;
    }

    const existing = (companyOptions || []).find(
      (c) => String(c.id) === String(id)
    );

    if (existing) {
      setSelectedCompany(existing);
      return;
    }

    getRequest(`${GET_URL.company.api}${id}/`, {}, props).then(
      (response) => {
        if (response && response.status === 200) {
          const row = response.data?.data || null;

          if (row && row.id != null) {
            setCompanyOptions((prev) => {
              const seen = new Set(
                (prev || []).map((r) => String(r.id))
              );

              if (seen.has(String(row.id))) {
                return prev || [];
              }

              return [row, ...(prev || [])];
            });

            setSelectedCompany(row);
          }
        }
      }
    );
  }, [form.company, props, companyOptions]);

  const setField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const submit = () => {
    if (!form.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Task title is required',
      });

      return;
    }

    if (!taskId && !form.task_type) {
      Swal.fire({
        icon: 'warning',
        title: 'Task type is required',
      });

      return;
    }

    const backlogSid = findBacklogStageId(workflowStages);

    let taskStageId = form.task_stage
      ? Number(form.task_stage)
      : null;

    if (form.task_type && !taskStageId && backlogSid) {
      taskStageId = Number(backlogSid);
    }

    if (!taskId && form.task_type && !taskStageId) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Backlog stage',
        text: 'This task type should have a stage with code "backlog".',
      });

      return;
    }

    const tt = workflowTypes.find(
      (t) => String(t.id) === String(form.task_type)
    );

    const categoryFromType =
      tt && LEGACY_CATEGORY_CODES.includes(tt.code)
        ? tt.code
        : 'dev_tasks';

    const category = form.task_type
      ? categoryFromType
      : legacyCategory;

    const payload = {
      title: form.title,
      description: form.description,
      company: form.company || null,
      assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
      category,
      priority: form.priority,
      task_type: form.task_type
        ? Number(form.task_type)
        : null,
      task_stage: taskStageId,
      deadline: form.deadline || null,
      planned_date: form.planned_date || null,
      estimated_hours: form.estimated_hours || 0,
      actual_hours: form.actual_hours || 0,
      recurring: form.recurring || 0,
      shift_type: form.recurring === 1 ? form.shift_type || null : null,
      trigger_time: form.recurring === 1 ? form.trigger_time || null : null,
      details: {
        images: form.images || [],
      },
    };

    setLoading(true);

    const request = taskId
      ? putRequest(
          `${PUT_URL.tasks.api}${taskId}/`,
          payload,
          props
        )
      : postRequest(
          POST_URL.tasks.api,
          [payload],
          props
        );

    request
      .then((response) => {
        setLoading(false);

        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: taskId
              ? 'Task updated'
              : 'Task created',
            showConfirmButton: false,
            timer: 1200,
          });

          navigate('/task/list');
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          {taskId ? 'Edit Task' : 'Create Task'}
        </Typography>

        <Button
          variant="outlined"
          onClick={() => navigate('/task/list')}
        >
          View Tasks
        </Button>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Pick a task type. New tasks default to Backlog
        stage.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          width: '100%',
          border: '1px solid #e5e7eb',
          background:
            'linear-gradient(to bottom, #ffffff, #fafafa)',
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Task Title"
              value={form.title}
              onChange={(e) =>
                setField('title', e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={companyOptions}
              value={selectedCompany}
              loading={companyLoading}
              onChange={(_, newValue) => {
                setSelectedCompany(newValue || null);

                setField('company', newValue?.id || '');
              }}
              inputValue={companySearch}
              onInputChange={(_, newInputValue) => {
                setCompanySearch(newInputValue || '');
              }}
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              getOptionLabel={(option) =>
                option?.name || ''
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Client (Optional)"
                  placeholder="Search client..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {companyLoading ? (
                          <CircularProgress
                            color="inherit"
                            size={18}
                          />
                        ) : null}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{
                onScroll: (event) => {
                  const listboxNode = event.currentTarget;

                  const nearBottom =
                    listboxNode.scrollTop +
                      listboxNode.clientHeight >=
                    listboxNode.scrollHeight - 24;

                  if (!nearBottom) return;
                  if (companyLoading) return;
                  if (!companyHasMore) return;

                  const nextPage = companyPage + 1;

                  setCompanyPage(nextPage);

                  loadCompanies(
                    nextPage,
                    companySearch,
                    'append'
                  );
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={workflowTypes}
              value={selectedTaskTypeOption}
              onChange={(_, newValue) => {
                const id =
                  newValue?.id != null
                    ? String(newValue.id)
                    : '';

                setSelectedTaskTypeOption(newValue);

                setForm((prev) => ({
                  ...prev,
                  task_type: id,
                  task_stage: '',
                }));
              }}
              isOptionEqualToValue={(a, b) =>
                String(a?.id) === String(b?.id)
              }
              getOptionLabel={(o) =>
                o && o.name ? o.name : ''
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="Task Type"
                  placeholder="Search or pick a type"
                />
              )}
              noOptionsText="No task types"
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {form.task_type &&
              findBacklogStageId(workflowStages)
                ? 'Workflow stage: Backlog (default)'
                : form.task_type
                ? 'Loading stages...'
                : 'Select a task type first.'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Assign To (Optional)"
              value={form.assigned_to}
              onChange={(e) =>
                setField('assigned_to', e.target.value)
              }
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.first_name || u.username}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Priority"
              value={form.priority}
              onChange={(e) =>
                setField('priority', e.target.value)
              }
            >
              {priorityOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Deadline"
              InputLabelProps={{ shrink: true }}
              value={form.deadline}
              onChange={(e) =>
                setField('deadline', e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Planned Date"
              InputLabelProps={{ shrink: true }}
              value={form.planned_date}
              onChange={(e) =>
                setField(
                  'planned_date',
                  e.target.value
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Estimated Hours"
              value={form.estimated_hours}
              onChange={(e) =>
                setField(
                  'estimated_hours',
                  e.target.value
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Actual Hours"
              value={form.actual_hours}
              onChange={(e) =>
                setField(
                  'actual_hours',
                  e.target.value
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Recurring"
              value={form.recurring}
              onChange={(e) =>
                setField('recurring', Number(e.target.value))
              }
            >
              <MenuItem value={0}>No</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
            </TextField>
          </Grid>

          {form.recurring === 1 && (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Shift Type"
                  value={form.shift_type}
                  onChange={(e) =>
                    setField('shift_type', e.target.value)
                  }
                >
                  <MenuItem value="weekwise">Week Wise</MenuItem>
                  <MenuItem value="monthwise">Month Wise</MenuItem>
                  <MenuItem value="yearwise">Year Wise</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="Trigger Time"
                  InputLabelProps={{ shrink: true }}
                  value={form.trigger_time}
                  onChange={(e) =>
                    setField('trigger_time', e.target.value)
                  }
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              Description
            </Typography>

            <Box
              sx={{
                background: '#fff',
                borderRadius: 2,
                overflow: 'hidden',

                '& .ql-toolbar': {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  background: '#f8fafc',
                },

                '& .ql-container': {
                  minHeight: 260,
                  fontSize: '15px',
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                },

                '& .ql-editor': {
                  minHeight: 220,
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={(value) =>
                  setField('description', value)
                }
                placeholder="Write task description..."
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Attachments (Images)
            </Typography>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                const base64Images = await Promise.all(
                  files.map((file) => {
                    return new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(file);
                      reader.onload = () => resolve(reader.result);
                      reader.onerror = (error) => reject(error);
                    });
                  })
                );
                setForm((prev) => ({
                  ...prev,
                  images: [...(prev.images || []), ...base64Images],
                }));
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              {form.images?.map((img, idx) => (
                <Box key={idx} sx={{ position: 'relative' }}>
                  <img src={img} alt={`task-${idx}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #ccc' }} />
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    sx={{ position: 'absolute', top: -5, right: -5, minWidth: 'auto', p: '2px 6px', fontSize: 10, borderRadius: '50%' }}
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== idx),
                      }));
                    }}
                  >
                    X
                  </Button>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 4,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/task/list')}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={submit}
          >
            {taskId ? 'Update Task' : 'Create Task'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default TaskAdd;